import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { isSameDay, parse, startOfWeek } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  QuestionMarkCircleIcon,
  PhotoIcon,
  UserIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import Notification from "../components/Notification";
import Tooltip from "../components/Tooltip";
import CurriculumProgressModal from "../components/CurriculumProgressModal";
import {
  getLocalYMDFromUTC,
  getTodayInStudioTz,
  getLocalDateString,
  getWeekStartInStudioTz,
  getWeekDays,
  formatTimeTo12Hour,
} from "../utils/timezone";

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert MM/DD/YYYY format to Date object
const convertDisplayDateToDate = (dateString: string): Date | null => {
  if (!dateString || dateString.trim() === '') return null;
  
  try {
    // Parse MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[0]) - 1; // Month is 0-indexed
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    
    return date;
  } catch (error) {
    return null;
  }
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  searchTerm: 100,
  dateInput: 10, // MM/DD/YYYY
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For search terms, preserve spaces but remove dangerous content
  if (fieldType === 'searchTerm') {
    // Only remove dangerous patterns, preserve spaces
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim to preserve user intent
  } else {
    // For other fields (like date inputs), use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'searchTerm':
      // Check for dangerous patterns in search terms
      const searchDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !searchDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'dateInput':
      // Check for MM/DD/YYYY format
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Attendance Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};

/** Use membership_type_ids from students table as source of truth for active membership. */
function hasActiveMembershipFromIds(personOrStudent: any): boolean {
  const ids = personOrStudent?.membership_type_ids;
  if (Array.isArray(ids) && ids.length > 0) return true;
  if (typeof ids === 'string') {
    try {
      const parsed = JSON.parse(ids);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  }
  return false;
}

// @ts-ignore
// TypeScript: allow use of Temporal global
// eslint-disable-next-line no-var
declare var Temporal: any;

interface Student {
  id: string;
  name: string;
  belt_id: string;
  belt_name: string;
  membership_id: string;
  membership_name: string;
  studio_id: string;
  belt?: string | null;
  student_image?: string | null;
}



interface ClassInstructor {
  id: string;
  name: string;
  belt: string | null;
  role: string | null;
}

interface Class {
  id: string;
  class_name: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  days: string[];
  assigned_instructors?: ClassInstructor[];
}

interface Event {
  id: string;
  event_name: string;
  from_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  type: string;
}

interface AttendanceRecord {
  id: string;
  student_id: string | null;
  lead_id: string | null;
  class_id: string | null;
  event_id: string | null;
  attended_at: string;
  notes: string | null;
  student?: Student;
  lead?: any;
}

export default function Attendance() {
  const { user, session, getAuthToken } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [leads, setLeads] = useState<any[]>([]); // For future use

  const [classes, setClasses] = useState<Class[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [instructors, setInstructors] = useState<ClassInstructor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dateInputString, setDateInputString] = useState("");
  const [selectedClassOrEvent, setSelectedClassOrEvent] = useState<any | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewAttendanceModal, setShowViewAttendanceModal] = useState(false);
  const [showAssignedStudentsModal, setShowAssignedStudentsModal] = useState(false);
  const [personPhotoModal, setPersonPhotoModal] = useState<{
    open: boolean;
    name: string;
    imageUrl: string | null;
  }>({ open: false, name: "", imageUrl: null });
  const [showCurriculumModal, setShowCurriculumModal] = useState(false);
  const [curriculumStudent, setCurriculumStudent] = useState<Student | null>(
    null
  );
  const [showClassCurriculumPicker, setShowClassCurriculumPicker] =
    useState(false);
  const [classCurriculumClass, setClassCurriculumClass] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [classCurriculumStudents, setClassCurriculumStudents] = useState<
    Student[]
  >([]);
  const [classCurriculumLoading, setClassCurriculumLoading] = useState(false);
  const [classCurriculumSearch, setClassCurriculumSearch] = useState("");
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]);
  const [selectedAssignedStudents, setSelectedAssignedStudents] = useState<any[]>([]);
  const [assignedStudentsLoading, setAssignedStudentsLoading] = useState(false);
  const [attendanceRecordsForComparison, setAttendanceRecordsForComparison] = useState<AttendanceRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeople, setSelectedPeople] = useState<any[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });
  const [membershipOverride, setMembershipOverride] = useState<{
    flow: "search" | "assigned";
    names: string;
    withoutMembershipPeople: any[];
    hadStudentsWithMembership?: boolean;
  } | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [studioId, setStudioId] = useState<string | null>(null);
  const [selectedAttendanceIds, setSelectedAttendanceIds] = useState<string[]>(
    []
  );
  const [showLeadsInAttendance, setShowLeadsInAttendance] = useState(true);
  const [studioTimezone, setStudioTimezone] =
    useState<string>("America/New_York");

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Belt promotion state
  const [selectedForBeltPromotion, setSelectedForBeltPromotion] = useState<Set<string>>(new Set());
  
  // Filter state
  const [showAssignedOnly, setShowAssignedOnly] = useState<boolean>(false);

  // Calculate the start of the week (Monday) based on selectedDate
  const weekStart = useMemo(() => {
    if (!studioTimezone) return new Date();
    // Convert selectedDate to studio timezone and get week start
    const dateString = selectedDate.toLocaleDateString("en-CA", { timeZone: studioTimezone });
    // Create date in studio timezone more safely
    const [year, month, day] = dateString.split('-').map(Number);
    const dateInStudioTz = new Date(year, month - 1, day); // month is 0-indexed
    
    // Validate the date before using it
    if (isNaN(dateInStudioTz.getTime())) {
      return new Date();
    }
    
    return startOfWeek(dateInStudioTz, { weekStartsOn: 1 });
  }, [selectedDate, studioTimezone]);

  const weekDays = useMemo(() => {
    return getWeekDays(weekStart);
  }, [weekStart]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        // eslint-disable-next-line no-console
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // 1. Ensure studioTimezone is loaded before any operations
  const loadData = useCallback(async () => {
    const authToken = getAuthToken();
    if (!user || !authToken) return;
    
    // Fetch CSRF token first
    await fetchCsrfToken();
    
    setAttendanceLoading(true);

    try {
      // Determine backend URL with smart fallback based on environment
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      // Fetch studio data FIRST to get timezone
      const studioResponse = await fetch(
        `${backendUrl}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!studioResponse.ok) {
        throw new Error("Failed to fetch studio data");
      }

      const studioData = await studioResponse.json();
      setStudioId(studioData.id);

      // Set timezone BEFORE doing any other operations
      const timezone = studioData.timezone || "America/New_York";
      setStudioTimezone(timezone);

      // Now fetch other data in parallel with higher limits to get all records
      const [studentsFirstPageRes, leadsFirstPageRes, classesResponse, eventsResponse, instructorsResponse] =
        await Promise.all([
          fetch(`${backendUrl}/api/supabase/students/${studioData.id}?page=1&limit=10000`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetch(`${backendUrl}/api/supabase/leads/${studioData.id}?page=1&limit=10000`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetch(`${backendUrl}/api/supabase/classes/${studioData.id}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetch(`${backendUrl}/api/supabase/events/${studioData.id}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          fetch(`${backendUrl}/api/supabase/instructors/${studioData.id}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }),
        ]);

      // Process responses...
      // Assemble all students across all pages
      if (studentsFirstPageRes.ok) {
        const studentsFirst = await studentsFirstPageRes.json();
        let allStudents = studentsFirst.data || [];
        const totalPages = studentsFirst.pagination?.totalPages || 1;
        const limit = studentsFirst.pagination?.limit || 15;
        if (totalPages > 1) {
          const pageFetches: Promise<Response>[] = [];
          for (let p = 2; p <= totalPages; p++) {
            pageFetches.push(
              fetch(`${backendUrl}/api/supabase/students/${studioData.id}?page=${p}&limit=${limit}`, {
                headers: { Authorization: `Bearer ${authToken}` },
              })
            );
          }
          const pageResponses = await Promise.all(pageFetches);
          const pageJsons = await Promise.all(pageResponses.map(r => r.json()));
          for (const pj of pageJsons) allStudents = allStudents.concat(pj.data || []);
        }
        setStudents(allStudents);
      }
      // Assemble all leads across all pages
      if (leadsFirstPageRes.ok) {
        const leadsFirst = await leadsFirstPageRes.json();
        let allLeads = leadsFirst.data || [];
        const totalPages = leadsFirst.pagination?.totalPages || 1;
        const limit = leadsFirst.pagination?.limit || 100;
        if (totalPages > 1) {
          const pageFetches: Promise<Response>[] = [];
          for (let p = 2; p <= totalPages; p++) {
            pageFetches.push(
              fetch(`${backendUrl}/api/supabase/leads/${studioData.id}?page=${p}&limit=${limit}`, {
                headers: { Authorization: `Bearer ${authToken}` },
              })
            );
          }
          const pageResponses = await Promise.all(pageFetches);
          const pageJsons = await Promise.all(pageResponses.map(r => r.json()));
          for (const pj of pageJsons) allLeads = allLeads.concat(pj.data || []);
        }
        setLeads(allLeads);
      }
      if (classesResponse.ok && instructorsResponse.ok) {
        const classesData = await classesResponse.json();
        const instructorsData = await instructorsResponse.json();
        
        // Hydrate assigned_instructors for each class
        const hydratedClasses = Array.isArray(classesData)
          ? classesData.map((classObj: any) => ({
              ...classObj,
              assigned_instructors: Array.isArray(classObj.assigned_instructors)
                ? classObj.assigned_instructors
                    .map((id: string) =>
                      instructorsData.find(
                        (inst: ClassInstructor) => inst.id === id
                      )
                    )
                    .filter(Boolean)
                : [],
            }))
          : [];
        
        setInstructors(instructorsData);
        setClasses(hydratedClasses);
      }
      if (eventsResponse.ok) {
        const eventsData = await eventsResponse.json();
        setEvents(eventsData || []);
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load data",
        title: "Error",
      });
    } finally {
      setAttendanceLoading(false);
    }
  }, [user, getAuthToken]);

  // 2. Initialize selectedDay based on studio timezone
  useEffect(() => {
    if (studioTimezone) {
      // Set selectedDay to today in studio timezone
      const today = getTodayInStudioTz(studioTimezone);
      setSelectedDay(today);
      setSelectedDate(today); // Also sync selectedDate
      
      // Format today's date as MM/DD/YYYY for the input
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const year = today.getFullYear();
      setDateInputString(`${month}/${day}/${year}`);
    }
  }, [studioTimezone]);

  // 3. Synchronize selectedDay when selectedDate changes
  useEffect(() => {
    if (selectedDate && studioTimezone) {
      // If selectedDay is not within the week of selectedDate, set it to selectedDate
      const weekDaysSet = new Set(weekDays.map((d) => d.toDateString()));
      if (!weekDaysSet.has(selectedDay.toDateString())) {
        setSelectedDay(selectedDate);
      }
    }
  }, [selectedDate, weekDays, selectedDay, studioTimezone]);

  // 4. Display attendance times in studio timezone
  const formatAttendanceTime = (utcTimestamp: string) => {
    if (!utcTimestamp) return "";
    const date = new Date(utcTimestamp);
    return date.toLocaleTimeString([], {
      timeZone: studioTimezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch lead visibility setting on mount
  useEffect(() => {
    const fetchLeadVisibility = async () => {
      if (!studioId || !session?.access_token) return;
      try {
        // Determine backend URL with smart fallback based on environment
        const isLocalDev =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const backendUrl = isLocalDev
          ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
          : process.env.REACT_APP_BACKEND_URL ||
            "https://student-management-system-1-na1m.onrender.com";

        const response = await fetch(
          `${backendUrl}/api/supabase/lead-visibility/${studioId}`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );
        if (!response.ok)
          throw new Error("Failed to fetch lead visibility setting");
        const setting = await response.json();
        setShowLeadsInAttendance(
          setting && typeof setting.is_enabled === "boolean"
            ? setting.is_enabled
            : true
        );
      } catch (err) {
        setShowLeadsInAttendance(false);
      }
    };
    fetchLeadVisibility();
  }, [studioId, session?.access_token]);

  // Helper: get classes/events for a given day
  const getWeekdayName = (date: Date) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  };
  const getClassesForDay = (day: Date) =>
    classes
      .filter(
        (c) => Array.isArray(c.days) && c.days.includes(getWeekdayName(day))
      )
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const getEventsForDay = (day: Date) => {
    if (!day || isNaN(day.getTime())) return [];
    const dayString = day.toISOString().slice(0, 10);
    return events.filter((e) => {
      // Check if the day falls within the event's date range
      return dayString >= e.from_date && dayString <= e.end_date;
    });
  };

  // Filtered students and leads for search
  const filteredStudents = students
    .filter((s) => s.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((s) => {
      // If showAssignedOnly is true and this is a class, only show assigned students
      if (showAssignedOnly && selectedClassOrEvent?.type === "class") {
        return assignedStudents.some(assigned => assigned.id === s.id);
      }
      return true; // Show all students if filter is off or not a class
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  const filteredLeads = leads
    .filter((l) => l.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const classCurriculumFilteredStudents = useMemo(() => {
    const q = classCurriculumSearch.trim().toLowerCase();
    const list = !q
      ? classCurriculumStudents
      : classCurriculumStudents.filter((s) =>
          s.name.toLowerCase().includes(q)
        );
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [classCurriculumStudents, classCurriculumSearch]);

  // Reset filter when modal is closed or class changes
  const resetFilter = () => {
    setShowAssignedOnly(false);
    setSearchTerm("");
  };

  // Add helper function to handle selection
  const togglePersonSelection = (person: any) => {
    setSelectedPeople((prev) => {
      const isSelected = prev.some(
        (p) => p.id === person.id && p.type === person.type
      );
      if (isSelected) {
        return prev.filter(
          (p) => !(p.id === person.id && p.type === person.type)
        );
      } else {
        return [...prev, person];
      }
    });
  };

  const closeClassCurriculumPicker = useCallback(() => {
    setShowClassCurriculumPicker(false);
    setClassCurriculumClass(null);
    setClassCurriculumStudents([]);
    setClassCurriculumSearch("");
    setClassCurriculumLoading(false);
  }, []);

  const fetchClassCurriculumStudents = useCallback(
    async (classId: string) => {
      const authToken = getAuthToken();
      if (!authToken || !classId || !studioId) return;

      const dateStr =
        selectedDay && !isNaN(selectedDay.getTime())
          ? selectedDay.toISOString().slice(0, 10)
          : "";
      if (!dateStr) {
        setClassCurriculumStudents([]);
        return;
      }

      setClassCurriculumLoading(true);
      setClassCurriculumStudents([]);
      try {
        const isLocalDev =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const backendUrl = isLocalDev
          ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
          : process.env.REACT_APP_BACKEND_URL ||
            "https://api.dojotrack.com";

        const url = `${backendUrl}/api/supabase/attendance/${studioId}?class_id=${encodeURIComponent(
          classId
        )}&date=${encodeURIComponent(dateStr)}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch attendance for this class");
        }

        const records = await response.json();
        const list: AttendanceRecord[] = Array.isArray(records) ? records : [];

        const seen = new Map<string, Student>();
        for (const r of list) {
          if (!r.student_id || seen.has(r.student_id)) continue;
          const full = students.find((s) => s.id === r.student_id);
          if (full) {
            seen.set(r.student_id, full);
            continue;
          }
          const nested = r.student as { id?: string; name?: string } | undefined;
          if (nested?.id) {
            seen.set(r.student_id, {
              id: nested.id,
              name: nested.name ?? "Student",
              belt_id: "",
              belt_name: "",
              membership_id: "",
              membership_name: "",
              studio_id: studioId,
              belt: null,
            });
          }
        }

        setClassCurriculumStudents(
          Array.from(seen.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        );
      } catch {
        setNotification({
          show: true,
          message: "Could not load who signed in to this class.",
          title: "Error",
        });
        setClassCurriculumStudents([]);
      } finally {
        setClassCurriculumLoading(false);
      }
    },
    [getAuthToken, studioId, selectedDay, students]
  );

  const openClassCurriculumPicker = useCallback(
    (c: Class) => {
      setClassCurriculumClass({ id: c.id, name: c.class_name });
      setClassCurriculumSearch("");
      setClassCurriculumStudents([]);
      setShowClassCurriculumPicker(true);
      void fetchClassCurriculumStudents(c.id);
    },
    [fetchClassCurriculumStudents]
  );

  const loadAttendanceRecords = useCallback(async () => {
    const authToken = getAuthToken();
    if (!user?.id || !authToken || !selectedClassOrEvent) return;
    setLoadingAttendance(true);
    try {
      // Determine backend URL with smart fallback based on environment
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      // For events, use the event's from_date. For classes, use the selected day
      const formattedDate =
        selectedClassOrEvent.type === "event"
          ? selectedClassOrEvent.from_date
          : (selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '');

      const url = `${backendUrl}/api/supabase/attendance/${
        selectedClassOrEvent.studio_id
      }?${
        selectedClassOrEvent.type === "class"
          ? `class_id=${selectedClassOrEvent.id}`
          : `event_id=${selectedClassOrEvent.id}`
      }&date=${formattedDate}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch attendance records");
      }

      const data = await response.json();
      setAttendanceRecords(data || []);
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load attendance records",
        title: "Error",
      });
    } finally {
      setLoadingAttendance(false);
    }
  }, [user?.id, getAuthToken, selectedClassOrEvent, selectedDay]);

  // Update the useEffect to only run when necessary
  useEffect(() => {
    if (showViewAttendanceModal && selectedClassOrEvent) {
      loadAttendanceRecords();
    }
  }, [showViewAttendanceModal, selectedClassOrEvent, loadAttendanceRecords]);

  const handleAddStudents = async () => {
    const authToken = getAuthToken();
    if (
      !studioId ||
      !authToken ||
      !selectedClassOrEvent ||
      selectedPeople.length === 0
    )
      return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before adding students again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addStudents' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    // Check for existing attendance records to prevent duplicates
    const peopleWithExistingAttendance = selectedPeople.filter(person => {
      return attendanceRecords.some(record => {
        const recordDate = new Date(record.attended_at);
        const selectedDateObj = selectedDay ? new Date(selectedDay) : new Date();
        const isSameDay = recordDate.toDateString() === selectedDateObj.toDateString();
        
        return (
          isSameDay &&
          record.student_id === person.id &&
          record.class_id === (selectedClassOrEvent.type === 'class' ? selectedClassOrEvent.id : null) &&
          record.event_id === (selectedClassOrEvent.type === 'event' ? selectedClassOrEvent.id : null)
        );
      });
    });

    if (peopleWithExistingAttendance.length > 0) {
      const names = peopleWithExistingAttendance.map(p => p.name).join(', ');
      setNotification({
        show: true,
        message: `The following people are already checked in for this ${selectedClassOrEvent.type} today: ${names}`,
        title: "Already Checked In",
      });
      return;
    }

    // Use membership_type_ids from students table as source of truth (one or more = has membership)
    const studentsWithoutMembership = selectedPeople.filter(
      (p) => p.type === "student" && !hasActiveMembershipFromIds(p)
    );
    const peopleWithMembershipOrLeads = selectedPeople.filter(
      (p) => p.type === "lead" || (p.type === "student" && hasActiveMembershipFromIds(p))
    );

    if (studentsWithoutMembership.length > 0) {
      if (peopleWithMembershipOrLeads.length > 0) {
        const peopleWithTimeInfoForWithMembership = peopleWithMembershipOrLeads.map((p) => {
          let dateString: string;
          let timeString: string;
          if (selectedClassOrEvent?.start_time) {
            if (selectedClassOrEvent.type === "event") {
              dateString = selectedClassOrEvent.from_date;
            } else {
              dateString = selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '';
            }
            const [hours, minutes, seconds] = selectedClassOrEvent.start_time.split(":").map(Number);
            timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
          } else {
            const nowInTz = getTodayInStudioTz(studioTimezone);
            dateString = nowInTz.toLocaleDateString("en-CA");
            timeString = nowInTz.toTimeString().slice(0, 8);
          }
          return {
            student_id: p.type === "student" ? p.id : null,
            lead_id: p.type === "lead" ? p.id : null,
            date_string: dateString,
            time_string: timeString,
          };
        });
        const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        const backendUrl = isLocalDev
          ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
          : process.env.REACT_APP_BACKEND_URL || "https://api.dojotrack.com";
        try {
          const addRes = await fetch(`${backendUrl}/api/supabase/attendance/add`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({
              class_id: selectedClassOrEvent.type === "class" ? selectedClassOrEvent.id : null,
              event_id: selectedClassOrEvent.type === "event" ? selectedClassOrEvent.id : null,
              studio_id: studioId,
              timezone: studioTimezone,
              people: peopleWithTimeInfoForWithMembership,
              csrfToken,
              skip_membership_check: false,
            }),
          });
          if (addRes.ok) {
            loadAttendanceRecords();
            if (selectedClassOrEvent?.type === "class") {
              fetchAssignedStudents(selectedClassOrEvent.id);
            }
          }
        } catch {
          // continue to show override modal
        }
      }
      const names = studentsWithoutMembership.map((p) => p.name).join(", ");
      setMembershipOverride({
        flow: "search",
        names,
        withoutMembershipPeople: studentsWithoutMembership,
        hadStudentsWithMembership: peopleWithMembershipOrLeads.length > 0,
      });
      return;
    }

    try {
      // For each person, compute attendedAt based on class/event start time and selected date
      const peopleWithTimeInfo = selectedPeople.map((p) => {
        let dateString: string;
        let timeString: string;

        if (selectedClassOrEvent?.start_time) {
          if (selectedClassOrEvent.type === "event") {
            // For events, use the event's from_date
            dateString = selectedClassOrEvent.from_date;
          } else {
            // For classes, use the selected day
            dateString = selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '';
          }

          const [hours, minutes, seconds] = selectedClassOrEvent.start_time
            .split(":")
            .map(Number);
          timeString = `${String(hours).padStart(2, "0")}:${String(
            minutes
          ).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
        } else {
          // Fallback to current time
          const nowInTz = getTodayInStudioTz(studioTimezone);
          dateString = nowInTz.toLocaleDateString("en-CA");
          timeString = nowInTz.toTimeString().slice(0, 8);
        }
        return {
          student_id: p.type === "student" ? p.id : null,
          lead_id: p.type === "lead" ? p.id : null,
          date_string: dateString,
          time_string: timeString,
        };
      });

      // Determine backend URL with smart fallback based on environment
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      const response = await fetch(
        `${backendUrl}/api/supabase/attendance/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            class_id:
              selectedClassOrEvent.type === "class"
                ? selectedClassOrEvent.id
                : null,
            event_id:
              selectedClassOrEvent.type === "event"
                ? selectedClassOrEvent.id
                : null,
            studio_id: studioId,
            timezone: studioTimezone,
            people: peopleWithTimeInfo,
            csrfToken,
            skip_membership_check: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || (response.status === 409 ? "Some students are already checked in for this class/event today" : "Failed to add students");
        throw new Error(message);
      }

      setNotification({
        show: true,
        message: "Students added successfully",
        title: "Success",
      });
      setShowAddModal(false);
      setPersonPhotoModal({ open: false, name: "", imageUrl: null });
      setShowCurriculumModal(false);
      setCurriculumStudent(null);
      closeClassCurriculumPicker();
      setSelectedPeople([]);
      resetFilter();
      loadAttendanceRecords();
      // Refresh assigned students data to update attendance status
      if (selectedClassOrEvent?.type === "class") {
        fetchAssignedStudents(selectedClassOrEvent.id);
      }
    } catch (error) {
      logSecurityEvent('ADD_STUDENTS_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        classId: selectedClassOrEvent?.id,
        eventId: selectedClassOrEvent?.id,
        peopleCount: selectedPeople.length
      });
      
      // Show the actual error message from the backend if available
      const errorMessage = error instanceof Error ? error.message : "Failed to add students";
      const isMembershipError = /membership/i.test(errorMessage);
      const isAlreadyCheckedIn = errorMessage.includes("already checked in");
      const title = isMembershipError ? "Membership Required" : isAlreadyCheckedIn ? "Already Checked In" : "Error";
      setNotification({
        show: true,
        message: errorMessage,
        title,
      });
    }
  };

  // Handler to toggle selection
  const toggleAttendanceSelection = (id: string) => {
    setSelectedAttendanceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Handler to remove selected attendance records
  const handleRemoveSelectedAttendance = async () => {
    const authToken = getAuthToken();
    if (selectedAttendanceIds.length === 0 || !authToken) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before removing attendance records again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'removeAttendance' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    try {
      // Determine backend URL with smart fallback based on environment
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      const response = await fetch(
        `${backendUrl}/api/supabase/attendance/remove`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ 
            ids: selectedAttendanceIds,
            csrfToken,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to remove attendance records");
      }
      setNotification({
        show: true,
        message: "Attendance records removed successfully",
        title: "Success",
      });
      setSelectedAttendanceIds([]);
      loadAttendanceRecords();
    } catch (error) {
      logSecurityEvent('REMOVE_ATTENDANCE_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        attendanceIds: selectedAttendanceIds
      });
      setNotification({
        show: true,
        message: "Failed to remove attendance records",
        title: "Error",
      });
    }
  };

  const fetchAssignedStudents = async (classId: string) => {
    const authToken = getAuthToken();
    if (!authToken || !classId) return;
    
    setAssignedStudentsLoading(true);
    try {
      // Determine backend URL with smart fallback based on environment
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      // Fetch both assigned students and current attendance records
      const [assignedResponse, attendanceResponse] = await Promise.all([
        fetch(
          `${backendUrl}/api/supabase/students/assigned-to-class/${classId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${backendUrl}/api/supabase/attendance/${selectedClassOrEvent?.studio_id}?class_id=${classId}&date=${selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : ''}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        )
      ]);

      if (!assignedResponse.ok) {
        throw new Error("Failed to fetch assigned students");
      }

      if (!attendanceResponse.ok) {
        throw new Error("Failed to fetch attendance records");
      }

      const assignedData = await assignedResponse.json();
      const attendanceData = await attendanceResponse.json();
      
      setAssignedStudents(assignedData || []);
      setAttendanceRecordsForComparison(attendanceData || []);
      setSelectedAssignedStudents([]);
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to fetch assigned students",
        title: "Error",
      });
    } finally {
      setAssignedStudentsLoading(false);
    }
  };

  const handleAssignedStudentToggle = (student: any) => {
    setSelectedAssignedStudents((prev) => {
      const isSelected = prev.some((s) => s.id === student.id);
      if (isSelected) {
        return prev.filter((s) => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  const isStudentAlreadySignedIn = (studentId: string) => {
    return attendanceRecordsForComparison.some(
      (record) => record.student_id === studentId
    );
  };

  const handleAddAssignedStudents = async () => {
    if (selectedAssignedStudents.length === 0) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before adding assigned students again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addAssignedStudents' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);
    
    // Check for existing attendance records to prevent duplicates
    const assignedStudentsWithExistingAttendance = selectedAssignedStudents.filter(student => {
      return attendanceRecords.some(record => {
        const recordDate = new Date(record.attended_at);
        const selectedDateObj = selectedDay ? new Date(selectedDay) : new Date();
        const isSameDay = recordDate.toDateString() === selectedDateObj.toDateString();
        
        return (
          isSameDay &&
          record.student_id === student.id &&
          record.class_id === (selectedClassOrEvent.type === 'class' ? selectedClassOrEvent.id : null) &&
          record.event_id === (selectedClassOrEvent.type === 'event' ? selectedClassOrEvent.id : null)
        );
      });
    });

    if (assignedStudentsWithExistingAttendance.length > 0) {
      const names = assignedStudentsWithExistingAttendance.map(s => s.name).join(', ');
      setNotification({
        show: true,
        message: `The following assigned students are already checked in for this ${selectedClassOrEvent.type} today: ${names}`,
        title: "Already Checked In",
      });
      return;
    }

    // Use membership_type_ids from students table as source of truth (one or more = has membership)
    const assignedWithoutMembership = selectedAssignedStudents.filter((s) => !hasActiveMembershipFromIds(s));
    const assignedWithMembership = selectedAssignedStudents.filter((s) => hasActiveMembershipFromIds(s));

    if (assignedWithoutMembership.length > 0) {
      if (assignedWithMembership.length > 0) {
        const peopleWithTimeInfoForWithMembership = assignedWithMembership.map((student: any) => {
          let dateString: string;
          let timeString: string;
          if (selectedClassOrEvent?.start_time) {
            if (selectedClassOrEvent.type === "event") {
              dateString = selectedClassOrEvent.from_date;
            } else {
              dateString = selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '';
            }
            const [hours, minutes, seconds] = selectedClassOrEvent.start_time.split(":").map(Number);
            timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
          } else {
            const nowInTz = getTodayInStudioTz(studioTimezone);
            dateString = nowInTz.toLocaleDateString("en-CA");
            timeString = nowInTz.toTimeString().slice(0, 8);
          }
          return {
            student_id: student.id,
            lead_id: null,
            date_string: dateString,
            time_string: timeString,
          };
        });
        const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        const backendUrl = isLocalDev
          ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
          : process.env.REACT_APP_BACKEND_URL || "https://api.dojotrack.com";
        const token = getAuthToken();
        try {
          const addRes = await fetch(`${backendUrl}/api/supabase/attendance/add`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({
              class_id: selectedClassOrEvent.type === "class" ? selectedClassOrEvent.id : null,
              event_id: selectedClassOrEvent.type === "event" ? selectedClassOrEvent.id : null,
              studio_id: studioId,
              timezone: studioTimezone,
              people: peopleWithTimeInfoForWithMembership,
              csrfToken,
              skip_membership_check: false,
            }),
          });
          if (addRes.ok) {
            loadAttendanceRecords();
            if (selectedClassOrEvent?.type === "class") {
              fetchAssignedStudents(selectedClassOrEvent.id);
            }
          }
        } catch {
          // continue to show override modal
        }
      }
      const names = assignedWithoutMembership.map((s) => s.name).join(", ");
      setMembershipOverride({
        flow: "assigned",
        names,
        withoutMembershipPeople: assignedWithoutMembership,
        hadStudentsWithMembership: assignedWithMembership.length > 0,
      });
      return;
    }

    // Convert assigned students to the format expected by handleAddStudents
    const peopleWithTimeInfo = selectedAssignedStudents.map((student) => {
      let dateString: string;
      let timeString: string;

      if (selectedClassOrEvent?.start_time) {
        if (selectedClassOrEvent.type === "event") {
          dateString = selectedClassOrEvent.from_date;
        } else {
          dateString = selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '';
        }

        const [hours, minutes, seconds] = selectedClassOrEvent.start_time
          .split(":")
          .map(Number);
        timeString = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
      } else {
        const nowInTz = getTodayInStudioTz(studioTimezone);
        dateString = nowInTz.toLocaleDateString("en-CA");
        timeString = nowInTz.toTimeString().slice(0, 8);
      }
      
      return {
        student_id: student.id,
        lead_id: null,
        date_string: dateString,
        time_string: timeString,
      };
    });

    // Use the existing add students functionality
    const authToken = getAuthToken();
    if (!studioId || !authToken || !selectedClassOrEvent) return;
    
    try {
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL ||
          "https://api.dojotrack.com";

      const response = await fetch(
        `${backendUrl}/api/supabase/attendance/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            class_id:
              selectedClassOrEvent.type === "class"
                ? selectedClassOrEvent.id
                : null,
            event_id:
              selectedClassOrEvent.type === "event"
                ? selectedClassOrEvent.id
                : null,
            studio_id: studioId,
            timezone: studioTimezone,
            people: peopleWithTimeInfo,
            csrfToken,
            skip_membership_check: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || (response.status === 409 ? "Some assigned students are already checked in for this class/event today" : "Failed to add assigned students");
        throw new Error(message);
      }

      setNotification({
        show: true,
        message: "Assigned students added to attendance successfully",
        title: "Success",
      });
      setShowAssignedStudentsModal(false);
      setSelectedAssignedStudents([]);
      resetFilter();
      loadAttendanceRecords();
      // Refresh assigned students data to update attendance status
      if (selectedClassOrEvent?.type === "class") {
        fetchAssignedStudents(selectedClassOrEvent.id);
      }
    } catch (error) {
      logSecurityEvent('ADD_ASSIGNED_STUDENTS_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        classId: selectedClassOrEvent?.id,
        eventId: selectedClassOrEvent?.id,
        assignedStudentsCount: selectedAssignedStudents.length
      });
      
      // Show the actual error message from the backend if available
      const errorMessage = error instanceof Error ? error.message : "Failed to add assigned students";
      const isMembershipError = /membership/i.test(errorMessage);
      const isAlreadyCheckedIn = errorMessage.includes("already checked in");
      const title = isMembershipError ? "Membership Required" : isAlreadyCheckedIn ? "Already Checked In" : "Error";
      setNotification({
        show: true,
        message: errorMessage,
        title,
      });
    }
  };

  // Admin override: sign in students without active membership (Yes on membership modal)
  const handleMembershipOverrideYes = async () => {
    if (!membershipOverride || !studioId || !selectedClassOrEvent) return;
    const authToken = getAuthToken();
    if (!authToken || !csrfToken) return;

    const buildPeopleWithTimeInfo = (list: any[], isSearch: boolean) => {
      return list.map((p: any) => {
        let dateString: string;
        let timeString: string;
        if (selectedClassOrEvent?.start_time) {
          if (selectedClassOrEvent.type === "event") {
            dateString = selectedClassOrEvent.from_date;
          } else {
            dateString = selectedDay && !isNaN(selectedDay.getTime()) ? selectedDay.toISOString().slice(0, 10) : '';
          }
          const [hours, minutes, seconds] = selectedClassOrEvent.start_time.split(":").map(Number);
          timeString = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;
        } else {
          const nowInTz = getTodayInStudioTz(studioTimezone);
          dateString = nowInTz.toLocaleDateString("en-CA");
          timeString = nowInTz.toTimeString().slice(0, 8);
        }
        return {
          student_id: isSearch ? (p.type === "student" ? p.id : null) : p.id,
          lead_id: isSearch ? (p.type === "lead" ? p.id : null) : null,
          date_string: dateString,
          time_string: timeString,
        };
      });
    };

    const flow = membershipOverride.flow;
    const listToAdd = membershipOverride.withoutMembershipPeople?.length
      ? membershipOverride.withoutMembershipPeople
      : flow === "search"
        ? selectedPeople
        : selectedAssignedStudents;
    const peopleWithTimeInfo = flow === "search"
      ? buildPeopleWithTimeInfo(listToAdd, true)
      : buildPeopleWithTimeInfo(listToAdd, false);

    setMembershipOverride(null);

    try {
      const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const backendUrl = isLocalDev
        ? process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"
        : process.env.REACT_APP_BACKEND_URL || "https://api.dojotrack.com";

      const response = await fetch(`${backendUrl}/api/supabase/attendance/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          class_id: selectedClassOrEvent.type === "class" ? selectedClassOrEvent.id : null,
          event_id: selectedClassOrEvent.type === "event" ? selectedClassOrEvent.id : null,
          studio_id: studioId,
          timezone: studioTimezone,
          people: peopleWithTimeInfo,
          csrfToken,
          skip_membership_check: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || "Failed to add students";
        throw new Error(message);
      }

      setNotification({
        show: true,
        message: flow === "assigned"
          ? "Assigned students added to attendance successfully"
          : "Students added successfully",
        title: "Success",
      });
      setShowAddModal(false);
      setPersonPhotoModal({ open: false, name: "", imageUrl: null });
      setShowCurriculumModal(false);
      setCurriculumStudent(null);
      closeClassCurriculumPicker();
      setSelectedPeople([]);
      setShowAssignedStudentsModal(false);
      setSelectedAssignedStudents([]);
      resetFilter();
      loadAttendanceRecords();
      if (selectedClassOrEvent?.type === "class") {
        fetchAssignedStudents(selectedClassOrEvent.id);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to add students";
      setNotification({ show: true, message: errorMessage, title: "Error" });
    }
  };

  // Handler for search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
    
    // Allow empty search terms (for clearing the search)
    if (sanitizedValue === '') {
      setSearchTerm(sanitizedValue);
      return;
    }
    
    // Enhanced validation only for non-empty values
    if (!validateInput(sanitizedValue, 'searchTerm')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'searchTerm', value: sanitizedValue.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid search term. Please check your input and try again.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits
    if (sanitizedValue.length > MAX_LENGTHS.searchTerm) {
      setNotification({
        show: true,
        message: `Search term too long. Maximum ${MAX_LENGTHS.searchTerm} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setSearchTerm(sanitizedValue);
  };

  // Handler for date input change
  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatDateInput(e.target.value);
    
    // Allow partial input while typing (don't validate incomplete dates)
    if (formattedValue.length < MAX_LENGTHS.dateInput) {
      setDateInputString(formattedValue);
      return;
    }
    
    // Only validate complete dates (MM/DD/YYYY format)
    if (formattedValue.length === MAX_LENGTHS.dateInput && !validateInput(formattedValue, 'dateInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'dateInput', value: formattedValue });
      setNotification({
        show: true,
        message: "Invalid date format. Please use MM/DD/YYYY format.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits (shouldn't happen with the above check, but safety)
    if (formattedValue.length > MAX_LENGTHS.dateInput) {
      setNotification({
        show: true,
        message: `Date input too long. Maximum ${MAX_LENGTHS.dateInput} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setDateInputString(formattedValue);
    
    // Convert to Date object and update selectedDate and selectedDay
    const dateObject = convertDisplayDateToDate(formattedValue);
    if (dateObject && studioTimezone) {
      // Convert the entered date to the studio's timezone more safely
      const dateString = dateObject.toLocaleDateString("en-CA", { timeZone: studioTimezone });
      const [year, month, day] = dateString.split('-').map(Number);
      const dateInStudioTz = new Date(year, month - 1, day); // month is 0-indexed
      
      // Validate the date before using it
      if (!isNaN(dateInStudioTz.getTime())) {
        setSelectedDate(dateInStudioTz);
        setSelectedDay(dateInStudioTz);
      } else {
        setSelectedDate(dateObject);
        setSelectedDay(dateObject);
      }
    } else if (dateObject) {
      // Fallback if no timezone is set yet
      setSelectedDate(dateObject);
      setSelectedDay(dateObject);
    }
  };

  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white text-gray-900`;
  };

  // Belt promotion functions
  const handleSelectForBeltPromotion = (studentId: string) => {
    setSelectedForBeltPromotion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAllForBeltPromotion = () => {
    const studentIds = attendanceRecords
      .filter((record) => record.student_id)
      .map((record) => record.student_id)
      .filter((id): id is string => id !== null);
    setSelectedForBeltPromotion(new Set(studentIds));
  };

  const handleDeselectAllForBeltPromotion = () => {
    setSelectedForBeltPromotion(new Set());
  };

  const handlePromoteSelectedStudents = async () => {
    if (selectedForBeltPromotion.size === 0) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before promoting students again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with promotion');
      }
    }

    setLastSubmissionTime(now);

    const authToken = getAuthToken();
    if (!authToken || !studioId) return;

    try {
      const studentIds = Array.from(selectedForBeltPromotion);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioId}/promote-students`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ studentIds }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        if (result.promoted && result.promoted.length > 0) {
          const promotedCount = result.promoted.length;
          const errorCount = result.errors.length;
          
          let message = `Successfully promoted ${promotedCount} student${promotedCount > 1 ? 's' : ''}`;
          if (errorCount > 0) {
            message += ` (${errorCount} error${errorCount > 1 ? 's' : ''} occurred)`;
          }
          
          setNotification({
            show: true,
            message,
            title: "Success",
          });
        } else if (result.errors && result.errors.length > 0) {
          setNotification({
            show: true,
            message: `Promotion failed: ${result.errors.join(', ')}`,
            title: "Error",
          });
        } else {
          setNotification({
            show: true,
            message: "No students were promoted",
            title: "Warning",
          });
        }
        
        // Clear selection
        setSelectedForBeltPromotion(new Set());
        
        // Refresh the attendance records to show updated belt information
        loadAttendanceRecords();
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          message: errorData.error || "Failed to promote students",
          title: "Error",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error promoting students",
        title: "Error",
      });
    }
  };

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        {/* Membership override modal: admin can choose to sign in students without active membership */}
        {membershipOverride && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Membership Required
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                  {membershipOverride.hadStudentsWithMembership && (
                    <>Students with active memberships have been checked in. </>
                  )}
                  The following students need an active membership to check in:{" "}
                  <span className="font-medium">{membershipOverride.names}</span>.
                  <br />
                  <br />
                  Sign them in anyway?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setMembershipOverride(null);
                      setShowAddModal(false);
                      setPersonPhotoModal({
                        open: false,
                        name: "",
                        imageUrl: null,
                      });
                      setShowCurriculumModal(false);
                      setCurriculumStudent(null);
                      closeClassCurriculumPicker();
                      setSelectedPeople([]);
                      setShowAssignedStudentsModal(false);
                      setSelectedAssignedStudents([]);
                      resetFilter();
                      loadAttendanceRecords();
                      if (selectedClassOrEvent?.type === "class") {
                        fetchAssignedStudents(selectedClassOrEvent.id);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleMembershipOverrideYes}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Yes, sign them in anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Header Section - always render this */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Attendance Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage class and event attendance
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Tooltip text="We automatically clean up attendance records older than 3 months to keep your account fast and organized. This helps maintain optimal performance while preserving your recent attendance data.">
              <div></div>
            </Tooltip>
            <div className="relative">
              <input
                type="text"
                maxLength={MAX_LENGTHS.dateInput}
                value={dateInputString}
                onChange={handleDateInputChange}
                placeholder="MM/DD/YYYY"
                className={getInputClassName(
                  "block pl-10 pr-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                )}
              />
              <CalendarIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Week of {weekStart.toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Loading spinner/message below header if loading */}
        {attendanceLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Loading attendance records...
              </div>
            </div>
          </div>
        )}

        {/* Only render the rest if not loading */}
        {!attendanceLoading && (
          <>
            {/* Weekday Selector */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2 xl:grid xl:grid-cols-7">
                {weekDays.map((day, index) => (
                  <button
                    key={day && !isNaN(day.getTime()) ? day.toISOString() : `day-${index}`}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all duration-200 flex-1 min-w-[calc(50%-0.25rem)] xl:flex-none xl:min-w-0 border ${
                      isSameDay(day, selectedDay)
                        ? "bg-indigo-600 text-white shadow-md border-indigo-700 dark:border-indigo-500"
                        : "bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600 shadow-sm"
                    }`}
                    onClick={() => setSelectedDay(day)}
                  >
                    <span className="text-sm font-medium">
                      {getWeekdayName(day)}
                    </span>
                    <span className="text-xs mt-1">
                      {day.toLocaleDateString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Classes and Events Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Classes */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <ClockIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Classes
                  </h2>
                </div>
                <div className="space-y-3">
                  {getClassesForDay(selectedDay).map((c) => (
                    <div
                      key={c.id}
                      className="flex w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-200 hover:border-indigo-200 dark:hover:border-indigo-500"
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left p-4 bg-gray-50 dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200"
                        onClick={() => {
                          setSelectedClassOrEvent({
                            ...c,
                            type: "class",
                          });
                          setShowModal(true);
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {c.class_name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {c.start_time
                                ? formatTimeTo12Hour(c.start_time)
                                : "?"}{" "}
                              -{" "}
                              {c.end_time ? formatTimeTo12Hour(c.end_time) : "?"}
                            </p>
                          </div>
                          <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        </div>
                      </button>
                      <button
                        type="button"
                        className="flex flex-shrink-0 items-center border-l border-gray-200 bg-gray-50 px-3 text-purple-600 transition-colors duration-200 hover:bg-indigo-50 dark:border-gray-600 dark:bg-gray-700 dark:text-purple-400 dark:hover:bg-indigo-900/20"
                        title="Curriculum for students who signed in to this class (this day)"
                        aria-label={`Curriculum progress for students who checked in to ${c.class_name} on the selected day`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openClassCurriculumPicker(c);
                        }}
                      >
                        <AcademicCapIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {getClassesForDay(selectedDay).length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No classes scheduled for this day
                    </div>
                  )}
                </div>
              </div>

              {/* Events */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <UserGroupIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Events
                  </h2>
                </div>
                <div className="space-y-3">
                  {getEventsForDay(selectedDay).map((e) => (
                    <button
                      key={e.id}
                      className="w-full text-left p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200 border border-gray-200 dark:border-gray-600 hover:border-indigo-200 dark:hover:border-indigo-500"
                      onClick={() => {
                        setSelectedClassOrEvent({
                          ...e,
                          type: "event",
                        });
                        setShowModal(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {e.event_name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {e.start_time
                              ? formatTimeTo12Hour(e.start_time)
                              : "?"}{" "}
                            -{" "}
                            {e.end_time ? formatTimeTo12Hour(e.end_time) : "?"}
                          </p>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                    </button>
                  ))}
                  {getEventsForDay(selectedDay).length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No events scheduled for this day
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Class/Event Attendance Modal */}
            {showModal && selectedClassOrEvent && (
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm">
                <div className="fixed inset-0 z-10 overflow-y-auto">
                  <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                      <div className="absolute right-0 top-0 pr-4 pt-4">
                        <button
                          type="button"
                          className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          onClick={() => setShowModal(false)}
                        >
                          <span className="sr-only pointer-events-none">Close</span>
                          <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                        </button>
                      </div>
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-left xl:text-center sm:ml-4 sm:mt-0 w-full">
                          <h3 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white mb-4 text-left xl:text-center">
                            {selectedClassOrEvent.type === "class"
                              ? "Class"
                              : "Event"}{" "}
                            Attendance List
                          </h3>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-left xl:text-center mt-2">
                            {(() => {
                              // Helper to format time in 12-hour am/pm
                              const formatTime = (t: string) => {
                                if (!t) return "";
                                // Handles both HH:mm and HH:mm:ss
                                const parsed = parse(
                                  t,
                                  t.length > 5 ? "HH:mm:ss" : "HH:mm",
                                  new Date()
                                );
                                return parsed.toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                              };
                              const start = formatTime(
                                selectedClassOrEvent.start_time
                              );
                              const end = formatTime(
                                selectedClassOrEvent.end_time
                              );
                              const timeStr = `${start} to ${end}`;
                              const name =
                                selectedClassOrEvent.type === "class"
                                  ? selectedClassOrEvent.class_name
                                  : selectedClassOrEvent.event_name;

                              if (selectedClassOrEvent.type === "class") {
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(selectedDay)} | {timeStr}
                                  </>
                                );
                              } else {
                                // For events, get day from event from_date
                                const eventDate = new Date(
                                  selectedClassOrEvent.from_date
                                );
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(eventDate)} | {timeStr}
                                  </>
                                );
                              }
                            })()}
                          </div>
                          
                          {/* Assigned Instructors Section - Only show for classes */}
                          {selectedClassOrEvent.type === "class" && selectedClassOrEvent.assigned_instructors && selectedClassOrEvent.assigned_instructors.length > 0 && (
                            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="text-left xl:text-center">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Assigned Instructors
                                </p>
                                <div className="flex flex-wrap justify-center gap-2">
                                  {selectedClassOrEvent.assigned_instructors.map((instructor: ClassInstructor) => (
                                    <span
                                      key={instructor.id}
                                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                                    >
                                      {instructor.name}
                                      {instructor.belt && (
                                        <span className="ml-1 text-indigo-600 dark:text-indigo-300">
                                          ({instructor.belt})
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-4 flex flex-col items-center space-y-4">
                            <button
                              type="button"
                              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                              onClick={() => {
                              setShowModal(false);
                              setShowAddModal(true);
                              // Automatically load assigned students data when modal opens
                              if (selectedClassOrEvent?.type === "class") {
                                fetchAssignedStudents(selectedClassOrEvent.id);
                              }
                            }}
                            >
                              <PlusIcon
                                className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none"
                                aria-hidden="true"
                              />
                              <span className="pointer-events-none">
                                {showLeadsInAttendance
                                  ? "Add Students / Leads"
                                  : "Add Students"}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center rounded-md bg-gray-200 dark:bg-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500"
                              onClick={() => {
                                setShowModal(false);
                                setShowViewAttendanceModal(true);
                              }}
                            >
                              <EyeIcon className="h-5 w-5 mr-1.5 pointer-events-none" />
                              <span className="pointer-events-none">View Attendance</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add Student Modal */}
            {showAddModal && (
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm">
                <div className="fixed inset-0 z-10 overflow-y-auto">
                  <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                      <div className="absolute right-0 top-0 pr-4 pt-4">
                        <button
                          type="button"
                          className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          onClick={() => {
                            setShowAddModal(false);
                            setSelectedPeople([]);
                            resetFilter();
                            setPersonPhotoModal({
                              open: false,
                              name: "",
                              imageUrl: null,
                            });
                            setShowCurriculumModal(false);
                            setCurriculumStudent(null);
                            closeClassCurriculumPicker();
                          }}
                        >
                          <span className="sr-only pointer-events-none">Close</span>
                          <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                        </button>
                      </div>
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-left xl:text-center sm:ml-4 sm:mt-0 w-full">
                          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white mb-4 text-left xl:text-center">
                            {selectedClassOrEvent.type === "class"
                              ? "Class"
                              : "Event"}{" "}
                            Attendance List
                          </h3>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-left xl:text-center mt-2">
                            {(() => {
                              // Helper to format time in 12-hour am/pm
                              const formatTime = (t: string) => {
                                if (!t) return "";
                                // Handles both HH:mm and HH:mm:ss
                                const parsed = parse(
                                  t,
                                  t.length > 5 ? "HH:mm:ss" : "HH:mm",
                                  new Date()
                                );
                                return parsed.toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                              };
                              const start = formatTime(
                                selectedClassOrEvent.start_time
                              );
                              const end = formatTime(
                                selectedClassOrEvent.end_time
                              );
                              const timeStr = `${start} to ${end}`;
                              const name =
                                selectedClassOrEvent.type === "class"
                                  ? selectedClassOrEvent.class_name
                                  : selectedClassOrEvent.event_name;

                              if (selectedClassOrEvent.type === "class") {
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(selectedDay)} | {timeStr}
                                  </>
                                );
                              } else {
                                // For events, get day from event from_date
                                const eventDate = new Date(
                                  selectedClassOrEvent.from_date
                                );
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(eventDate)} | {timeStr}
                                  </>
                                );
                              }
                                                          })()}
                          </div>
                          
                          {/* Assigned Instructors and Total Available - Side by side */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Assigned Instructors Section - Only show for classes */}
                            {selectedClassOrEvent.type === "class" && selectedClassOrEvent.assigned_instructors && selectedClassOrEvent.assigned_instructors.length > 0 && (
                              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="text-left xl:text-center">
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Assigned Instructors
                                  </p>
                                  <div className="flex flex-wrap xl:justify-center gap-2">
                                    {selectedClassOrEvent.assigned_instructors.map((instructor: ClassInstructor) => (
                                      <span
                                        key={instructor.id}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                                      >
                                        {instructor.name}
                                        {instructor.belt && (
                                          <span className="ml-1 text-indigo-600 dark:text-indigo-300">
                                            ({instructor.belt})
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Total Available Section */}
                            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="text-left xl:text-center">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Total Available
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  <span className="font-semibold text-gray-900 dark:text-white">{filteredStudents.length + (showLeadsInAttendance ? filteredLeads.length : 0)}</span>
                                </p>
                                <div className="flex justify-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  <span>Students: {filteredStudents.length}</span>
                                  {showLeadsInAttendance && (
                                    <span>Leads: {filteredLeads.length}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="relative mb-4">
                              <input
                                type="text"
                                maxLength={MAX_LENGTHS.searchTerm}
                                placeholder={showLeadsInAttendance ? "Search students or leads..." : "Search students..."}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={getInputClassName(
                                  "block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white"
                                )}
                              />
                            </div>
                            {/* Filter toggle for assigned students - only show for classes */}
                            {selectedClassOrEvent?.type === "class" && (
                              <div className="mb-4 flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => setShowAssignedOnly(!showAssignedOnly)}
                                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                    showAssignedOnly
                                      ? "bg-blue-600 text-white hover:bg-blue-700"
                                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                                  }`}
                                >
                                  <UserGroupIcon className="h-4 w-4 mr-2 pointer-events-none" />
                                  <span className="pointer-events-none">{showAssignedOnly ? "Show All Students" : "Show Assigned Only"}</span>
                                </button>
                                {showAssignedOnly && (
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {filteredStudents.length} assigned students
                                  </span>
                                )}
                              </div>
                            )}
                            {selectedClassOrEvent.type === "class" && (
                              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                <div className="flex items-center">
                                  <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                    Assigned Students
                                  </span>
                                  {assignedStudentsLoading && (
                                    <div className="ml-2 flex items-center">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                      <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">Loading...</span>
                                    </div>
                                  )}
                                </div>
                                {assignedStudents.length > 0 && !assignedStudentsLoading && (
                                  <div className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                                    {assignedStudents.length} students assigned to this class
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="space-y-4">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                  {showAssignedOnly && selectedClassOrEvent?.type === "class" 
                                    ? `Assigned Students (${filteredStudents.length})`
                                    : `Students (${filteredStudents.length})`
                                  }
                                </h4>
                                <div className="max-h-64 overflow-y-auto">
                                  {filteredStudents.length === 0 ? (
                                    <div className="p-4 text-left xl:text-center text-gray-500 dark:text-gray-400">
                                      No students found
                                    </div>
                                  ) : (
                                    <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                                      {filteredStudents.map((s) => {
                                        const isAssigned = assignedStudents.some(assigned => assigned.id === s.id);
                                        const isSignedIn = isStudentAlreadySignedIn(s.id);
                                        return (
                                          <li key={s.id}>
                                            <div
                                              role="button"
                                              tabIndex={0}
                                              className={`w-full px-4 py-2 text-left flex items-center border rounded-md mb-2 transition-colors duration-150 ${
                                                selectedPeople.some(
                                                  (p) =>
                                                    p.id === s.id &&
                                                    p.type === "student"
                                                )
                                                  ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-500 text-gray-900 dark:text-white font-semibold"
                                                  : isAssigned
                                                  ? isSignedIn
                                                    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-500 text-gray-900 dark:text-white"
                                                    : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-500 text-gray-900 dark:text-white"
                                                  : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                                              }`}
                                              onClick={() =>
                                                togglePersonSelection({
                                                  ...s,
                                                  type: "student",
                                                })
                                              }
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === "Enter" ||
                                                  e.key === " "
                                                ) {
                                                  e.preventDefault();
                                                  togglePersonSelection({
                                                    ...s,
                                                    type: "student",
                                                  });
                                                }
                                              }}
                                            >
                                              <div
                                                className={`h-4 w-4 rounded border mr-3 flex-shrink-0 ${
                                                  selectedPeople.some(
                                                    (p) =>
                                                      p.id === s.id &&
                                                      p.type === "student"
                                                  )
                                                    ? "bg-indigo-600 border-indigo-600"
                                                    : "border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600"
                                                }`}
                                              >
                                                {selectedPeople.some(
                                                  (p) =>
                                                    p.id === s.id &&
                                                    p.type === "student"
                                                ) && (
                                                  <svg
                                                    className="h-4 w-4 text-white"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                  >
                                                    <path
                                                      fillRule="evenodd"
                                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                      clipRule="evenodd"
                                                    />
                                                  </svg>
                                                )}
                                              </div>
                                              <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                                                <div className="flex items-center gap-1 min-w-0">
                                                  <span className="truncate">
                                                    {s.name}
                                                  </span>
                                                  <button
                                                    type="button"
                                                    className="flex-shrink-0 p-1 rounded-md text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    aria-label={`View photo for ${s.name}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setPersonPhotoModal({
                                                        open: true,
                                                        name: s.name,
                                                        imageUrl:
                                                          s.student_image ??
                                                          null,
                                                      });
                                                    }}
                                                  >
                                                    <PhotoIcon className="h-5 w-5" />
                                                  </button>
                                                </div>
                                                <div className="flex items-center space-x-2 flex-shrink-0">
                                                  {isAssigned && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                                      Assigned
                                                    </span>
                                                  )}
                                                  {isAssigned && !isSignedIn && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                                      ✗ Not signed in
                                                    </span>
                                                  )}
                                                  {isSignedIn && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                                      ✓ Signed in
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </div>
                              </div>
                              {showLeadsInAttendance && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                    Leads ({filteredLeads.length})
                                  </h4>
                                  <div className="max-h-64 overflow-y-auto">
                                    {filteredLeads.length === 0 ? (
                                    <div className="p-4 text-left xl:text-center text-gray-500 dark:text-gray-400">
                                        No leads found
                                      </div>
                                    ) : (
                                      <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                                        {filteredLeads.map((l) => (
                                          <li key={l.id}>
                                            <div
                                              role="button"
                                              tabIndex={0}
                                              className={`w-full px-4 py-2 text-left flex items-center border rounded-md mb-2 transition-colors duration-150 ${
                                                selectedPeople.some(
                                                  (p) =>
                                                    p.id === l.id &&
                                                    p.type === "lead"
                                                )
                                                  ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-500 text-gray-900 dark:text-white font-semibold"
                                                  : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
                                              }`}
                                              onClick={() =>
                                                togglePersonSelection({
                                                  ...l,
                                                  type: "lead",
                                                })
                                              }
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === "Enter" ||
                                                  e.key === " "
                                                ) {
                                                  e.preventDefault();
                                                  togglePersonSelection({
                                                    ...l,
                                                    type: "lead",
                                                  });
                                                }
                                              }}
                                            >
                                              <div
                                                className={`h-4 w-4 rounded border mr-3 flex-shrink-0 ${
                                                  selectedPeople.some(
                                                    (p) =>
                                                      p.id === l.id &&
                                                      p.type === "lead"
                                                  )
                                                    ? "bg-indigo-600 border-indigo-600"
                                                    : "border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600"
                                                }`}
                                              >
                                                {selectedPeople.some(
                                                  (p) =>
                                                    p.id === l.id &&
                                                    p.type === "lead"
                                                ) && (
                                                  <svg
                                                    className="h-4 w-4 text-white"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                  >
                                                    <path
                                                      fillRule="evenodd"
                                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                      clipRule="evenodd"
                                                    />
                                                  </svg>
                                                )}
                                              </div>
                                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                                <span className="truncate">
                                                  {l.name}
                                                </span>
                                                <button
                                                  type="button"
                                                  className="flex-shrink-0 p-1 rounded-md text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                  aria-label={`View photo for ${l.name}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPersonPhotoModal({
                                                      open: true,
                                                      name: l.name,
                                                      imageUrl:
                                                        l.lead_image ??
                                                        null,
                                                    });
                                                  }}
                                                >
                                                  <PhotoIcon className="h-5 w-5" />
                                                </button>
                                              </div>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-6 flex items-center justify-between">
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedPeople.length} selected
                              </div>
                              <button
                                type="button"
                                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={selectedPeople.length === 0}
                                onClick={handleAddStudents}
                              >
                                <span className="pointer-events-none">
                                  Add to{" "}
                                  {selectedClassOrEvent?.type === "class"
                                    ? "Class"
                                    : "Event"}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {personPhotoModal.open && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-500 bg-opacity-75 backdrop-blur-sm p-4"
                onClick={() =>
                  setPersonPhotoModal({
                    open: false,
                    name: "",
                    imageUrl: null,
                  })
                }
              >
                <div
                  className="relative w-full max-w-sm rounded-lg bg-white dark:bg-gray-800 px-6 pb-6 pt-8 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="absolute right-3 top-3 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    onClick={() =>
                      setPersonPhotoModal({
                        open: false,
                        name: "",
                        imageUrl: null,
                      })
                    }
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <div className="flex flex-col items-center text-center">
                    {personPhotoModal.imageUrl ? (
                      <img
                        src={personPhotoModal.imageUrl}
                        alt={`${personPhotoModal.name} photo`}
                        className="h-40 w-40 rounded-full object-cover border border-gray-300 dark:border-gray-600"
                      />
                    ) : (
                      <div className="h-40 w-40 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center border border-gray-300 dark:border-gray-600">
                        <UserIcon className="h-20 w-20 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                      {personPhotoModal.name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showClassCurriculumPicker && classCurriculumClass && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-500 bg-opacity-75 backdrop-blur-sm p-4"
                onClick={() => closeClassCurriculumPicker()}
              >
                <div
                  className="relative w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 px-4 py-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Curriculum progress
                      </h3>
                      <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {classCurriculumClass.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Signed in for {selectedDay && !isNaN(selectedDay.getTime())
                          ? selectedDay.toLocaleDateString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "this day"}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-md p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => closeClassCurriculumPicker()}
                      aria-label="Close"
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="p-4">
                    <input
                      type="text"
                      maxLength={MAX_LENGTHS.searchTerm}
                      placeholder="Search students..."
                      value={classCurriculumSearch}
                      onChange={(e) =>
                        setClassCurriculumSearch(
                          sanitizeInput(e.target.value, "searchTerm")
                        )
                      }
                      disabled={classCurriculumLoading}
                      className={getInputClassName(
                        "block w-full rounded-md border-0 py-1.5 px-3 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 sm:text-sm dark:bg-gray-700 dark:text-white disabled:opacity-50"
                      )}
                    />
                    {classCurriculumLoading ? (
                      <div className="mt-6 flex flex-col items-center justify-center py-8">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Loading students…
                        </p>
                      </div>
                    ) : (
                      <ul className="mt-3 max-h-72 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-600 rounded-md border border-gray-200 dark:border-gray-600">
                        {classCurriculumFilteredStudents.length === 0 ? (
                          <li className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            {classCurriculumStudents.length === 0
                              ? "No students have signed in to this class for this day yet."
                              : "No students match your search."}
                          </li>
                        ) : (
                          classCurriculumFilteredStudents.map((s) => (
                            <li key={s.id}>
                              <button
                                type="button"
                                className="flex w-full px-3 py-2.5 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                  setCurriculumStudent(s);
                                  closeClassCurriculumPicker();
                                  setShowCurriculumModal(true);
                                }}
                              >
                                {s.name}
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showCurriculumModal && curriculumStudent && (
              <CurriculumProgressModal
                open={showCurriculumModal}
                onClose={() => {
                  setShowCurriculumModal(false);
                  setCurriculumStudent(null);
                }}
                studentId={curriculumStudent.id}
                studentName={curriculumStudent.name}
                studentBelt={curriculumStudent.belt ?? null}
              />
            )}

            {/* View Attendance Modal */}
            {showViewAttendanceModal && selectedClassOrEvent && (
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm">
                <div className="fixed inset-0 z-10 overflow-y-auto">
                  <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                      <div className="absolute right-0 top-0 pr-4 pt-4">
                        <button
                          type="button"
                          className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                          onClick={() => setShowViewAttendanceModal(false)}
                        >
                          <span className="sr-only pointer-events-none">Close</span>
                          <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                        </button>
                      </div>
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-left xl:text-center sm:ml-4 sm:mt-0 w-full">
                          <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white mb-4 text-left xl:text-center">
                            {selectedClassOrEvent.type === "class"
                              ? "Class"
                              : "Event"}{" "}
                            Attendance List
                          </h3>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-left xl:text-center mt-2">
                            {(() => {
                              // Helper to format time in 12-hour am/pm
                              const formatTime = (t: string) => {
                                if (!t) return "";
                                // Handles both HH:mm and HH:mm:ss
                                const parsed = parse(
                                  t,
                                  t.length > 5 ? "HH:mm:ss" : "HH:mm",
                                  new Date()
                                );
                                return parsed.toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                              };
                              const start = formatTime(
                                selectedClassOrEvent.start_time
                              );
                              const end = formatTime(
                                selectedClassOrEvent.end_time
                              );
                              const timeStr = `${start} to ${end}`;
                              const name =
                                selectedClassOrEvent.type === "class"
                                  ? selectedClassOrEvent.class_name
                                  : selectedClassOrEvent.event_name;

                              if (selectedClassOrEvent.type === "class") {
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(selectedDay)} | {timeStr}
                                  </>
                                );
                              } else {
                                // For events, get day from event from_date
                                const eventDate = new Date(
                                  selectedClassOrEvent.from_date
                                );
                                return (
                                  <>
                                    <span className="font-medium">{name}</span>{" "}
                                    | {getWeekdayName(eventDate)} | {timeStr}
                                  </>
                                );
                              }
                                                          })()}
                          </div>
                          
                          {/* Assigned Instructors and Total Attendance - Side by side */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Assigned Instructors Section - Only show for classes */}
                            {selectedClassOrEvent.type === "class" && selectedClassOrEvent.assigned_instructors && selectedClassOrEvent.assigned_instructors.length > 0 && (
                              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="text-left xl:text-center">
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Assigned Instructors
                                  </p>
                                  <div className="flex flex-wrap xl:justify-center gap-2">
                                    {selectedClassOrEvent.assigned_instructors.map((instructor: ClassInstructor) => (
                                      <span
                                        key={instructor.id}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                                      >
                                        {instructor.name}
                                        {instructor.belt && (
                                          <span className="ml-1 text-indigo-600 dark:text-indigo-300">
                                            ({instructor.belt})
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Total Attendance Section */}
                            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="text-left xl:text-center">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Total Attendance
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  <span className="font-semibold text-gray-900 dark:text-white">{attendanceRecords.length}</span>
                                </p>
                                <div className="flex justify-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  <span>Students: {attendanceRecords.filter((record) => record.student_id).length}</span>
                                  {showLeadsInAttendance && (
                                    <span>Leads: {attendanceRecords.filter((record) => record.lead_id).length}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="mb-4 flex items-center justify-between">
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedAttendanceIds.length} selected
                              </span>
                              <button
                                type="button"
                                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={selectedAttendanceIds.length === 0}
                                onClick={handleRemoveSelectedAttendance}
                              >
                                <span className="pointer-events-none">Remove Selected</span>
                              </button>
                            </div>
                            <div className="space-y-4">
                              {/* Students Section */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                    Students ({attendanceRecords.filter((record) => record.student_id).length})
                                  </h4>
                                  {/* Belt Promotion Section - Only show for Testing events */}
                                  {selectedClassOrEvent.type === "event" && selectedClassOrEvent.event_name?.includes("Testing") && (
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={handleSelectAllForBeltPromotion}
                                        className="px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      >
                                        <span className="pointer-events-none">Select All</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleDeselectAllForBeltPromotion}
                                        className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                      >
                                        <span className="pointer-events-none">Deselect All</span>
                                      </button>
                                      {selectedForBeltPromotion.size > 0 && (
                                        <button
                                          type="button"
                                          onClick={handlePromoteSelectedStudents}
                                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                        >
                                          <span className="pointer-events-none">Promote Selected ({selectedForBeltPromotion.size})</span>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {/* Belt Promotion Info - Only show for Testing events */}
                                {selectedClassOrEvent.type === "event" && selectedClassOrEvent.event_name?.includes("Testing") && (
                                  <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                                    <p className="text-xs text-blue-700 dark:text-blue-300">
                                      <strong>Belt Promotion:</strong> Use the green checkboxes to select students who passed their belt test. 
                                      Click "Promote Selected" to advance them to the next belt rank. Students will be promoted based on the belt order configured in Settings.
                                    </p>
                                  </div>
                                )}
                                <div className="bg-white dark:bg-gray-700 shadow overflow-hidden sm:rounded-md">
                                  {loadingAttendance ? (
                                    <div className="p-4 text-center">
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
                                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                        Loading attendance...
                                      </p>
                                    </div>
                                  ) : (
                                    <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                                      {attendanceRecords
                                        .filter((record) => record.student_id)
                                        .sort((a, b) =>
                                          (a.student?.name || "").localeCompare(
                                            b.student?.name || ""
                                          )
                                        )
                                        .map((record) => (
                                          <li
                                            key={record.id}
                                            className="px-4 py-3 flex items-center"
                                          >
                                            <input
                                              type="checkbox"
                                              className="mr-3 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-500 rounded"
                                              checked={selectedAttendanceIds.includes(
                                                record.id
                                              )}
                                              onChange={() =>
                                                toggleAttendanceSelection(
                                                  record.id
                                                )
                                              }
                                            />
                                            {/* Belt Promotion Checkbox - Only show for Testing events */}
                                            {selectedClassOrEvent.type === "event" && selectedClassOrEvent.event_name?.includes("Testing") && record.student_id && (
                                              <input
                                                type="checkbox"
                                                className="mr-3 h-4 w-4 text-green-600 border-2 border-green-500 dark:border-green-400 rounded focus:ring-green-500 focus:ring-2"
                                                checked={selectedForBeltPromotion.has(record.student_id)}
                                                onChange={() =>
                                                  record.student_id && handleSelectForBeltPromotion(record.student_id)
                                                }
                                                title="Select for belt promotion"
                                              />
                                            )}
                                            <div className="flex-1 flex items-center justify-between">
                                              <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                  {record.student?.name ||
                                                    "Unknown Student"}
                                                  {/* Show current belt for belt testing events */}
                                                  {selectedClassOrEvent.type === "event" && selectedClassOrEvent.event_name?.includes("Testing") && record.student?.belt && (
                                                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                                      ({record.student.belt})
                                                    </span>
                                                  )}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                  {new Date(
                                                    record.attended_at
                                                  ).toLocaleTimeString([], {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                    timeZone: studioTimezone,
                                                  })}
                                                </p>
                                              </div>
                                              {record.notes && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                                  {record.notes}
                                                </p>
                                              )}
                                            </div>
                                          </li>
                                        ))}
                                      {attendanceRecords.filter(
                                        (record) => record.student_id
                                      ).length === 0 && (
                                        <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                          No students have signed in yet
                                        </li>
                                      )}
                                    </ul>
                                  )}
                                </div>
                              </div>
                              {/* Leads Section */}
                              {showLeadsInAttendance && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                    Leads ({attendanceRecords.filter((record) => record.lead_id).length})
                                  </h4>
                                  <div className="bg-white dark:bg-gray-700 shadow overflow-hidden sm:rounded-md">
                                    {loadingAttendance ? (
                                      <div className="p-4 text-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
                                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                          Loading attendance...
                                        </p>
                                      </div>
                                    ) : (
                                      <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                                        {attendanceRecords
                                          .filter((record) => record.lead_id)
                                          .sort((a, b) =>
                                            (a.lead?.name || "").localeCompare(
                                              b.lead?.name || ""
                                            )
                                          )
                                          .map((record) => (
                                            <li
                                              key={record.id}
                                              className="px-4 py-3 flex items-center"
                                            >
                                              <input
                                                type="checkbox"
                                                className="mr-3 h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-500 rounded"
                                                checked={selectedAttendanceIds.includes(
                                                  record.id
                                                )}
                                                onChange={() =>
                                                  toggleAttendanceSelection(
                                                    record.id
                                                  )
                                                }
                                              />
                                              <div className="flex-1 flex items-center justify-between">
                                                <div>
                                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {record.lead?.name ||
                                                      "Unknown Lead"}
                                                  </p>
                                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {new Date(
                                                      record.attended_at
                                                    ).toLocaleTimeString([], {
                                                      hour: "numeric",
                                                      minute: "2-digit",
                                                      hour12: true,
                                                      timeZone: studioTimezone,
                                                    })}
                                                  </p>
                                                </div>
                                                {record.notes && (
                                                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                                    {record.notes}
                                                  </p>
                                                )}
                                              </div>
                                            </li>
                                          ))}
                                        {attendanceRecords.filter(
                                          (record) => record.lead_id
                                        ).length === 0 && (
                                          <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                            No leads have signed in yet
                                          </li>
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </>
        )}
      </div>
    </div>
  );
}
